import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';

/** Shared upload ceiling — keep pages and the backend limit in sync with this. */
export const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_UPLOAD_SIZE_LABEL = '10MB';

const DEFAULT_ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

function extensionList(acceptMap) {
  const names = Object.values(acceptMap)
    .flat()
    .map(ext => ext.replace('.', '').toUpperCase());
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/**
 * The forwarded handle exposes `focus()` on the dropzone, so a form that blocks on
 * a missing file can send the user to it the same way it would to an input.
 */
const FileUpload = forwardRef(function FileUpload({
  value, onChange, accept, label, error, required, fileLabel = 'File',
}, ref) {
  const [rejectionError, setRejectionError] = useState(null);
  const zoneRef = useRef(null);
  const returnFocus = useRef(false);
  const reactId = useId();
  const inputId = `file-upload-${reactId}`;
  const errorId = `${inputId}-error`;
  const acceptMap = accept || DEFAULT_ACCEPT;
  const accepted = extensionList(acceptMap);

  const onDrop = useCallback(files => {
    if (files.length > 0) {
      setRejectionError(null);
      onChange(files[0]);
    }
  }, [onChange]);

  // Without this, react-dropzone drops oversized/wrong-type files on the floor
  // and the field just looks like nothing happened.
  const onDropRejected = useCallback(rejections => {
    const rejection = rejections[0];
    if (!rejection) return;
    const codes = (rejection.errors || []).map(e => e.code);
    if (codes.includes('file-too-large')) {
      const mb = (rejection.file.size / (1024 * 1024)).toFixed(1);
      setRejectionError(`${fileLabel} is ${mb}MB; maximum is ${MAX_UPLOAD_SIZE_LABEL}`);
    } else if (codes.includes('file-invalid-type')) {
      setRejectionError(`Only ${accepted} are accepted`);
    } else if (codes.includes('too-many-files')) {
      setRejectionError('Please upload a single file');
    } else {
      setRejectionError(`${fileLabel} could not be accepted`);
    }
  }, [accepted, fileLabel]);

  useImperativeHandle(ref, () => ({ focus: () => zoneRef.current?.focus() }), []);

  // Clearing a file unmounts the button that was clicked, which drops focus to
  // <body> and leaves a keyboard user at the top of the page mid-task.
  useEffect(() => {
    if (value || !returnFocus.current) return;
    returnFocus.current = false;
    zoneRef.current?.focus();
  }, [value]);

  const clearFile = () => {
    returnFocus.current = true;
    setRejectionError(null);
    onChange(null);
  };

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    onDropRejected,
    accept: acceptMap,
    maxFiles: 1,
    maxSize: MAX_UPLOAD_SIZE_BYTES,
  });

  const { ref: dropzoneRef } = getRootProps();
  const shownError = error || rejectionError || (fileRejections.length > 0 ? `${fileLabel} could not be accepted` : null);
  const dropzoneLabel = `${label || `Upload ${fileLabel}`}. Drag and drop a file or press Enter to browse. Accepted formats: ${accepted}. Maximum size ${MAX_UPLOAD_SIZE_LABEL}.`;

  return (
    <div>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && !String(label).includes('*') && (
            <span aria-hidden="true" className="ml-0.5 font-normal text-danger-500">*</span>
          )}
        </label>
      )}
      {value ? (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <DocumentArrowUpIcon className="h-5 w-5 text-brand-600" aria-hidden="true" />
          <span className="text-sm text-slate-700 flex-1 truncate">{value.name}</span>
          <span className="text-xs text-slate-500">{(value.size / 1024).toFixed(0)} KB</span>
          <button
            type="button"
            onClick={clearFile}
            aria-label={`Remove ${value.name}`}
            className="-m-1 rounded-lg p-1.5 text-slate-500 transition-colors duration-150 hover:bg-danger-50 hover:text-danger-700 active:bg-danger-100 focus-ring"
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <div
          {...getRootProps({
            role: 'button',
            'aria-label': dropzoneLabel,
            'aria-describedby': shownError ? errorId : undefined,
            'aria-invalid': shownError ? 'true' : undefined,
          })}
          // Dropzone hands back its own ref; keep that one fed and take a copy, so
          // this node can be focused from outside the component too.
          ref={(node) => {
            zoneRef.current = node;
            if (dropzoneRef) dropzoneRef.current = node;
          }}
          // A mouse drag gets a full state change; keyboard focus got a ring alone.
          className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors duration-150 focus-ring focus-visible:border-brand-500 focus-visible:bg-brand-50 ${
            isDragActive ? 'border-brand-500 bg-brand-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
          }`}
        >
          <input {...getInputProps({ id: inputId })} />
          <DocumentArrowUpIcon className="h-8 w-8 text-slate-400 mb-2" aria-hidden="true" />
          <p className="text-sm text-slate-600">
            {isDragActive ? 'Drop file here' : 'Drag & drop or click to upload'}
          </p>
          <p className="text-xs text-slate-500 mt-1">{accepted} (max {MAX_UPLOAD_SIZE_LABEL})</p>
        </div>
      )}
      {shownError && (
        <div role="alert">
          <p id={errorId} className="mt-1.5 text-sm font-medium text-danger-700">{shownError}</p>
        </div>
      )}
    </div>
  );
});

export default FileUpload;
